import { Check, Unlink } from "lucide-react";
import { useEffect, useState } from "react";
import { useCurrentUserGoogleIntegration } from "@/contexts/CurrentUserGoogleIntegrationContext";

export function GoogleIntegrationSettings() {
  const { integration, fetching, refetch } = useCurrentUserGoogleIntegration();
  const [disconnecting, setDisconnecting] = useState(false);

  // The shell-level provider serves a cached value; refresh once on mount so
  // the settings page always reflects current state (e.g. after returning from
  // the Google OAuth round-trip in another tab).
  useEffect(() => {
    refetch();
  }, [refetch]);

  const handleConnect = () => {
    window.location.href = "/oauth/google/connect?return_to=/app/settings?tab=integrations";
  };

  const handleDisconnect = async () => {
    if (
      !window.confirm(
        "Disconnect your Google account? Reports will fail to export until you reconnect.",
      )
    )
      return;
    setDisconnecting(true);
    try {
      const csrf =
        (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content ?? "";
      await fetch("/oauth/google/disconnect", {
        method: "DELETE",
        headers: { "X-CSRF-Token": csrf, Accept: "application/json" },
        credentials: "same-origin",
      });
      refetch();
    } finally {
      setDisconnecting(false);
    }
  };

  if (fetching) {
    return <div className="p-4 text-sm text-text-muted">Loading…</div>;
  }

  return (
    <div className="space-y-4 rounded-lg bg-card p-6 shadow-sm ring-1 ring-ring">
      <div>
        <h3 className="text-sm font-semibold text-text-primary">Google Docs</h3>
        <p className="mt-1 text-sm text-text-muted">
          Connect your Google account to export reports as Google Docs. Documents are created in
          your personal Drive — you fully own and can share them.
        </p>
      </div>

      {integration ? (
        <div className="flex items-center justify-between rounded border border-green-200 bg-green-50 px-3 py-2">
          <div className="flex items-center gap-2 text-sm text-green-900">
            <Check size={16} />
            <span>
              Connected as <strong>{integration.googleEmail}</strong>
            </span>
          </div>
          <button
            type="button"
            onClick={handleDisconnect}
            disabled={disconnecting}
            className="inline-flex items-center gap-1 rounded border border-green-300 bg-white px-2 py-1 text-xs font-medium text-green-900 hover:bg-green-100 disabled:opacity-50"
          >
            <Unlink size={12} />
            {disconnecting ? "Disconnecting…" : "Disconnect"}
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={handleConnect}
          className="inline-flex items-center gap-2 rounded bg-accent px-3 py-2 text-sm font-medium text-white hover:bg-accent/90"
        >
          Connect Google
        </button>
      )}
    </div>
  );
}
